// src/components/ProjectMembersModal.tsx
import React, { useState, useEffect } from "react";
import { FiX, FiUser, FiSearch, FiPlus, FiUsers, FiTrash2 } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { confirm } from "../../lib/dialogs";
import { 
  getProjectMembers, 
  addProjectMember, 
  removeProjectMember,
  getCompanyMembers
} from "../../services/roles";
import { useUserRole } from "../../hooks/useUserRole";
import type { Project, ProjectMember, CompanyMember } from "../../types/domain";
import "../ProjectMembersModal.css";

interface Props {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function ProjectMembersModal({ project, isOpen, onClose }: Props) {
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [availableMembers, setAvailableMembers] = useState<CompanyMember[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const { isAdminOfCompany } = useUserRole();

  useEffect(() => {
    if (isOpen && project) {
      loadMembers();
      loadAvailableMembers();
    }
  }, [isOpen, project]);

  const loadMembers = async () => {
    if (!project) return;
    
    setLoading(true);
    const { data, error } = await getProjectMembers(project.id);
    if (error) {
      toast.error("Error cargando miembros del proyecto");
    } else if (data) {
      setMembers(data);
    }
    setLoading(false);
  };

  const loadAvailableMembers = async () => {
    if (!project) return;

    const { data, error } = await getCompanyMembers(project.company_id);
    if (!error && data) {
      // Filter out users that are already project members
      const memberUserIds = new Set(members.map(m => m.user_id));
      const filtered = data.filter(member => 
        !memberUserIds.has(member.user_id) &&
        (searchQuery === "" || 
         member.user?.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         member.user?.email?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
      setAvailableMembers(filtered);
    }
  };

  useEffect(() => {
    if (showAddMember) {
      loadAvailableMembers();
    }
  }, [searchQuery, showAddMember, members]);

  const handleAddMember = async (userId: string) => {
    if (!project) return;

    try {
      const { data, error } = await addProjectMember(project.id, userId);
      if (error) throw error;

      if (data) {
        setMembers(prev => [...prev, data]);
        setShowAddMember(false);
        setSearchQuery("");
        toast.success("Miembro añadido al proyecto");
      }
    } catch (error) {
      console.error("Error adding project member:", error);
      toast.error("Error añadiendo miembro al proyecto");
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    const confirmed = await confirm({
      title: "Eliminar miembro del proyecto",
      text: "¿Estás seguro de eliminar este miembro del proyecto?",
      icon: "warning",
      confirmText: "Eliminar",
    });
    
    if (!confirmed) return;

    try {
      const { error } = await removeProjectMember(memberId);
      if (error) throw error;

      setMembers(prev => prev.filter(m => m.id !== memberId));
      toast.success("Miembro eliminado del proyecto");
    } catch (error) {
      console.error("Error removing project member:", error);
      toast.error("Error eliminando miembro del proyecto");
    }
  };

  const canManageProjectMembers = project ? isAdminOfCompany(project.company_id) : false;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="project-members-modal" onClick={e => e.stopPropagation()}>
        <div className="project-members-modal-header">
          <h2>
            <FiUsers size={20} />
            Miembros del proyecto: {project?.name}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FiX size={18} />
          </button>
        </div>

        <div className="project-members-modal-content">
          {/* Add Member Section */}
          {canManageProjectMembers && (
            <div className="add-member-section">
              {!showAddMember ? (
                <button 
                  className="add-member-btn"
                  onClick={() => setShowAddMember(true)}
                >
                  <FiPlus size={16} />
                  Asignar Miembro al Proyecto
                </button>
              ) : (
                <div className="add-member-form">
                  <div className="search-section">
                    <div className="search-input-container">
                      <FiSearch size={16} />
                      <input
                        type="text"
                        placeholder="Buscar miembros de la empresa..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="search-input"
                      />
                    </div>
                    <button 
                      className="cancel-add-btn"
                      onClick={() => {
                        setShowAddMember(false);
                        setSearchQuery("");
                      }}
                    >
                      Cancelar
                    </button>
                  </div>

                  <div className="available-members">
                    {availableMembers.map(member => (
                      <div key={member.id} className="member-item">
                        <div className="member-info">
                          <div className="member-avatar">
                            {member.user?.avatar_url ? (
                              <img src={member.user.avatar_url} alt={member.user.full_name || ""} />
                            ) : (
                              <FiUser size={16} />
                            )}
                          </div>
                          <div className="member-details">
                            <span className="member-name">
                              {member.user?.full_name || "Sin nombre"}
                            </span>
                            <span className="member-email">{member.user?.email}</span>
                            <span className="member-role">
                              {member.role === 'admin' ? 'Administrador' : 'Miembro'} de la empresa
                            </span>
                          </div>
                        </div>
                        <button 
                          className="assign-btn"
                          onClick={() => handleAddMember(member.user_id)}
                        >
                          Asignar
                        </button>
                      </div>
                    ))}
                    {availableMembers.length === 0 && (
                      <div className="no-members">
                        {searchQuery ? "No se encontraron miembros" : "No hay miembros disponibles para asignar"}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Project Members List */}
          <div className="project-members-list">
            <h3>Miembros asignados ({members.length})</h3>
            {loading ? (
              <div className="loading">Cargando miembros...</div>
            ) : (
              members.map(member => (
                <div key={member.id} className="project-member-item">
                  <div className="member-info">
                    <div className="member-avatar">
                      {member.user?.avatar_url ? (
                        <img src={member.user.avatar_url} alt={member.user.full_name || ""} />
                      ) : (
                        <FiUser size={20} />
                      )}
                    </div>
                    <div className="member-details">
                      <span className="member-name">
                        {member.user?.full_name || "Sin nombre"}
                      </span>
                      <span className="member-email">{member.user?.email}</span>
                      <span className="assigned-date">
                        Asignado el {new Date(member.created_at || "").toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  {canManageProjectMembers && (
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveMember(member.id)}
                      title="Eliminar del proyecto"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  )}
                </div>
              ))
            )}

            {!loading && members.length === 0 && (
              <div className="no-members">
                <FiUsers size={48} />
                <p>Este proyecto no tiene miembros asignados todavía</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}